import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ReviewFileStatus } from '@common/reviews/enums/review-file-status.enum';
import { ReviewFileUserStatus } from '@common/reviews/enums/review-file-user-status.enum';
import { ReviewFileUser } from '@common/reviews/models/review-file-user.model';
import { ReviewFile } from '@common/reviews/models/review-file.model';
import { CollabSessionSyncState } from '@common/sockets/enums/collab-session-sync-state.enum';
import { PropertyObservable } from '@common/util/property-observable.decorator';
import { ErrorService } from '@portal-core/errors/services/error.service';
import { ConfirmDialogComponent, ConfirmDialogData } from '@portal-core/general/components/confirm-dialog/confirm-dialog.component';
import { ErrorDialogComponent } from '@portal-core/general/components/error-dialog/error-dialog.component';
import { FlareTextEditorComponent } from '@portal-core/project-files/components/flare-text-editor/flare-text-editor.component';
import { ReviewFileUsersService } from '@portal-core/reviews/review-file-users/services/review-file-users.service';
import { InputObservable } from '@portal-core/util/input-observable.decorator';
import { Observable, combineLatest, filter, from, map, of, startWith, switchMap, tap } from 'rxjs';

@Component({
  selector: 'mc-flare-file-reviewer-toolbar-content',
  templateUrl: './flare-file-reviewer-toolbar-content.component.html',
  styleUrls: ['./flare-file-reviewer-toolbar-content.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FlareFileReviewerToolbarContentComponent implements OnInit {
  @Input() flareTextEditor: FlareTextEditorComponent;
  @Input() reviewFile: ReviewFile;
  @Input() reviewFileUser: ReviewFileUser;

  @Output() reviewerStatusUpdate: EventEmitter<ReviewFileUserStatus> = new EventEmitter<ReviewFileUserStatus>();

  @InputObservable('flareTextEditor') flareTextEditor$: Observable<FlareTextEditorComponent>;
  @PropertyObservable('submitting') submitting$: Observable<boolean>;

  CollabSessionSyncState: typeof CollabSessionSyncState = CollabSessionSyncState;
  ReviewFileUserStatus: typeof ReviewFileUserStatus = ReviewFileUserStatus;
  ReviewFileStatus: typeof ReviewFileStatus = ReviewFileStatus;

  reviewSyncState$: Observable<CollabSessionSyncState>;
  submitting: boolean;
  submitDisabled$: Observable<boolean>;

  constructor(
    private reviewFileUsersService: ReviewFileUsersService,
    private errorService: ErrorService,
    private dialog: MatDialog
  ) { }

  ngOnInit() {
    this.reviewSyncState$ = this.flareTextEditor$.pipe(
      switchMap(flareTextEditor => flareTextEditor ? flareTextEditor.syncStateChange : of(null))
    );

    // Create the observable for disabling the submit button
    this.submitDisabled$ = combineLatest([
      this.flareTextEditor$.pipe(
        switchMap(flareTextEditor => flareTextEditor ? flareTextEditor.errorChange : of(null)),
        startWith(null)
      ),
      this.reviewSyncState$,
      this.submitting$
    ]).pipe(
      // Disable the submit button if the doc is in error, the review sync state has not started or is loading, or if a submission is in progress
      map(([error, reviewSyncState, submitting]) => {
        return !!error || !reviewSyncState || reviewSyncState === CollabSessionSyncState.Loading || submitting;
      })
    );
  }

  onSubmitClicked() {
    // Store the review file id because the save is asynchronous meaning there is a chance that the review file will have changed by the time it has saved.
    const reviewFileId = this.reviewFile.Id;
    let continueWithSave$: Observable<boolean>;

    // If the doc is too big to sync then prompt the user to see if they still want to submit
    if (this.flareTextEditor.docExceedsMaxSize || this.flareTextEditor.docExceedsMongoDBMaxSize) {
      continueWithSave$ = this.dialog.open<ConfirmDialogComponent, ConfirmDialogData, boolean>(ConfirmDialogComponent, {
        width: '36rem',
        data: {
          action: 'Submit',
          title: 'Submit Review File',
          prompt: 'You have unsaved changes. Do you still want to Submit?'
        }
      }).afterClosed();
    } else {
      continueWithSave$ = of(true);
    }

    continueWithSave$.pipe(
      filter(continueWithSave => continueWithSave),
      tap(() => this.submitting = true),
      switchMap(() => from(this.flareTextEditor.save())),
      switchMap(() => this.reviewFileUsersService.updateMyReviewFileUserStatus$(reviewFileId, this.reviewFileUser.Id, ReviewFileUserStatus.Submitted))
    ).subscribe({
      next: () => {
        this.submitting = false;
        this.flareTextEditor.markAsPristine();
        this.reviewerStatusUpdate.emit(ReviewFileUserStatus.Submitted);
      },
      error: error => {
        this.submitting = false;
        this.showError(error, 'Unable to submit the review file. Please try again later. If the problem persists, please contact Technical Support.');
      }
    });
  }

  private showError(error: any, errorMessage: string) {
    this.dialog.open(ErrorDialogComponent, {
      ...ErrorDialogComponent.DialogConfig,
      data: {
        title: 'Changes Not Saved',
        message: errorMessage,
        errors: this.errorService.getErrorMessages(error)
      }
    });
  }
}
